/**
 * F5.3 Wave-4 — T-SQL data-type vocabulary and position detection.
 *
 * Surfaces the curated T-SQL data type list at the "column-type" / "parameter-
 * type" / "CAST target" / "CONVERT first-arg" slots. The grammar's clause
 * classifier returns `'unknown'` at these positions (none of the standard
 * CompletionPositionKind arms fits — they're not WHERE / SELECT-list /
 * group-by / etc.), so the resolver invokes `isMssqlDataTypePosition` as a
 * narrow text-walk fallback to recognise:
 *
 *   1. `CREATE TABLE foo (colName |` — column-list inside CREATE TABLE
 *      (persistent, temp `#tmp`, table-variable `DECLARE @t TABLE`).
 *   2. `DECLARE @x |` — scalar local declaration.
 *   3. `CAST(expr AS |` — CAST target type.
 *   4. `TRY_CAST(expr AS |` — TRY_CAST target type.
 *   5. `CONVERT(|` — CONVERT first argument.
 *   6. `CREATE [OR ALTER] PROC[EDURE]/FUNCTION ... (@param |` — routine
 *      parameter declaration.
 *
 * Pure text-walk — no tokenizer dependencies. Operates on the slice of the
 * source text from `statementStart` up to the cursor.
 */

import { dropAttachedPartialTokens, tokenize, type Token } from '../context/tokenizer';
import { isConstraintLeadWord } from './constraintLeadWords';

const ENGINE = 'mssql' as const;

/**
 * Curated T-SQL data type vocabulary surfaced at column-type / CAST-target
 * positions. The list aligns with the types named in the autocomplete
 * coverage scripts (29 / 33 / 34 / 35 / 41 / 42 / 45 / 53 / 58) and covers
 * the common-case categories: integer, decimal, money, float, date/time,
 * string (unicode + non-unicode), bit, uniqueidentifier, xml, binary.
 *
 * `MAX` is intentionally NOT in this list — it's a length specifier inside
 * `NVARCHAR(MAX)` / `VARBINARY(MAX)`, not a top-level type. The keyword list
 * already carries it for completeness.
 */
const MSSQL_DATA_TYPES: readonly string[] = [
  'INT',
  'BIGINT',
  'SMALLINT',
  'TINYINT',
  'BIT',
  'DECIMAL',
  'NUMERIC',
  'MONEY',
  'SMALLMONEY',
  'FLOAT',
  'REAL',
  'DATE',
  'DATETIME',
  'DATETIME2',
  'SMALLDATETIME',
  'DATETIMEOFFSET',
  'TIME',
  'CHAR',
  'NCHAR',
  'VARCHAR',
  'NVARCHAR',
  'TEXT',
  'NTEXT',
  'BINARY',
  'VARBINARY',
  'IMAGE',
  'UNIQUEIDENTIFIER',
  'XML',
  'SQL_VARIANT',
  'ROWVERSION',
  'TIMESTAMP',
  'HIERARCHYID',
  'GEOGRAPHY',
  'GEOMETRY',
];

export function getMssqlDataTypes(): readonly string[] {
  return MSSQL_DATA_TYPES;
}

/**
 * Detect whether the cursor sits at a T-SQL "data-type" slot. Returns true
 * for the six shapes documented in the module header. Conservative — when in
 * doubt the function returns false so regular emission still fires.
 *
 * The detector uses the resolver's shared `tokenize` helper. We slice
 * `[statementStart, offset)` so cross-statement noise can't trip the heuristic
 * (the resolver already isolates the cursor's statement via its statement-
 * range walk).
 *
 * TYPED-PREFIX PARITY: a bare word the caret is ATTACHED to (`CAST(x AS NVA▮`,
 * `DECLARE @x IN▮`) is the type being typed, not context — it is dropped via
 * {@link dropAttachedPartialTokens} so the attached-partial caret classifies
 * exactly like the bare one. Without the drop the tail token is the partial
 * and every shape below misses, so the type list vanished on the first
 * keystroke (mssql coverage script.33 / 34 / 35 / 39-42 / 53 / 58).
 */
export function isMssqlDataTypePosition(
  source: string,
  cursor: number,
  statementStart: number,
): boolean {
  const raw = tokenize(source, statementStart, cursor, ENGINE);
  if (raw.length === 0) return false;
  const tokens = dropAttachedPartialTokens(raw, cursor);
  if (tokens.length === 0) return false;
  // The cursor's "previous significant token" is the last non-whitespace
  // entry in the slice; `tokenize` already skips whitespace, so we use the
  // tail.
  const prev = tokens[tokens.length - 1];
  if (!prev) return false;

  // Shape 4: ... AS |  (CAST(expr AS |) / TRY_CAST(expr AS |))
  // Trigger only when the AS sits inside an unclosed `(` whose opener is
  // preceded by CAST / TRY_CAST. Read from the DROPPED tail (`AS NVA▮`) and
  // from the RAW one (a caret attached to `AS` itself — `CAST(x AS▮` — has
  // always fired here and keeps doing so).
  if (identUpper(prev) === 'AS' || identUpper(raw[raw.length - 1]) === 'AS') {
    if (isInsideCallTo(tokens, ['CAST', 'TRY_CAST'])) return true;
  }

  // Shape 5: CONVERT(|   or CONVERT(\s*  — cursor sits right after the
  // opening `(` of a CONVERT call (the first arg slot is the data type).
  if (prev.type === 'lparen') {
    // The opener must be preceded by CONVERT (token immediately before lparen).
    const before = tokens[tokens.length - 2];
    if (before && identUpper(before) === 'CONVERT') return true;
  }

  // Shape 6: PARSE(... AS |   — handled by Shape 4 (PARSE shares the
  // `AS <type>` shape with CAST). No extra branch needed.

  // Shape 1 — CREATE TABLE column-list (or CREATE TABLE #tmp / DECLARE @t TABLE).
  // Pattern: cursor sits inside an unclosed `(` whose opener follows a
  // CREATE TABLE / TABLE keyword, and the token immediately before the
  // cursor is an identifier (the column name, bare or quoted).
  if (prev.type === 'ident') {
    // Skip `@`-prefixed identifiers — those are handled by Shape 2 / 6
    // below (DECLARE / parameter-decl).
    if (!prev.text.startsWith('@') && !isConstraintLeadWord(prev.text)) {
      if (isInsideCreateTableColumnList(tokens)) return true;
    }
  }

  // Shape 6 — CREATE PROC/FUNCTION parameter-decl. Pattern: the cursor's
  // previous token is an `@`-prefixed local AND the cursor sits inside an
  // unclosed `(` of a CREATE PROCEDURE / FUNCTION.
  // Shape 2 — DECLARE @x |. Pattern: the cursor's previous token is an
  // `@`-prefixed local AND it follows a `DECLARE` keyword (no enclosing
  // paren — that's the scalar-local form).
  if (prev.type === 'ident' && prev.text.startsWith('@')) {
    if (isInsideCreateRoutineParamList(tokens)) return true;
    if (followsCreateProcedureHeader(tokens)) return true;
    if (followsDeclare(tokens)) return true;
  }

  return false;
}

// ─────────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────────

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}

/**
 * Walk back from the cursor over a balanced stack of parens. Returns the
 * index of the unclosed `(` enclosing the cursor, or -1 when the cursor is
 * outside any unbalanced paren.
 */
function findEnclosingLparenIndex(tokens: readonly Token[]): number {
  let depth = 0;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    if (t.type === 'rparen') depth += 1;
    else if (t.type === 'lparen') {
      if (depth === 0) return i;
      depth -= 1;
    }
  }
  return -1;
}

/**
 * True when the cursor sits inside an unclosed `(` whose immediately preceding
 * token is one of `callNames` (case-insensitive). Used to detect `CAST(...|`
 * shapes (the AS-arg inside).
 */
function isInsideCallTo(
  tokens: readonly Token[],
  callNames: readonly string[],
): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  const before = tokens[lparenIdx - 1];
  const upper = identUpper(before);
  if (!upper) return false;
  return callNames.includes(upper);
}

/**
 * True when the cursor sits inside an unclosed `(` whose enclosing statement
 * is a `CREATE TABLE` or `DECLARE @x TABLE`. We look for the keyword sequence
 * before the unclosed `(`: TABLE keyword anywhere among the few tokens
 * preceding the opener.
 */
function isInsideCreateTableColumnList(tokens: readonly Token[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx <= 0) return false;
  // Walk back from the opener looking for a `TABLE` keyword within the
  // preceding ~8 tokens; reject if we hit a CALL-like identifier first.
  let sawTable = false;
  for (let i = lparenIdx - 1; i >= Math.max(0, lparenIdx - 12); i -= 1) {
    const u = identUpper(tokens[i]);
    if (u === 'TABLE') {
      sawTable = true;
      break;
    }
    // Stop at semicolons, commas, or other parens — they signal we've left
    // the CREATE TABLE preamble.
    const t = tokens[i]!;
    if (t.type === 'lparen' || t.type === 'rparen' || t.type === 'comma') break;
  }
  if (!sawTable) return false;
  // Validate that the prev (column-name) sits at a "fresh column slot" — its
  // previous significant token must be the column-list `(` or a `,` separator.
  // Otherwise the prev is mid-clause (e.g. `REFERENCES`, `CONSTRAINT`,
  // `FOREIGN KEY`) and the data-type emitter must NOT fire — those slots are
  // ddl-fk-target / ddl-references-column-list / column references. Pinned by
  // goals/autocomplete-coverage mssql script.29 §3.
  const beforePrev = tokens[tokens.length - 2];
  if (!beforePrev) return false;
  return beforePrev.type === 'lparen' || beforePrev.type === 'comma';
}

/**
 * True when the cursor sits inside an unclosed `(` whose enclosing statement
 * is a CREATE [OR ALTER] PROC[EDURE] / FUNCTION header. Look for those
 * keywords in the first few tokens of the slice.
 */
function isInsideCreateRoutineParamList(tokens: readonly Token[]): boolean {
  const lparenIdx = findEnclosingLparenIndex(tokens);
  if (lparenIdx < 0) return false;
  // CREATE PROCEDURE / FUNCTION header: the relevant tokens sit at the head
  // of the statement; we scan the prefix before the opener for the keyword
  // sequence.
  const head = tokens.slice(0, lparenIdx);
  const upperHead = head.map((t) => identUpper(t)).filter((u): u is string => !!u);
  let i = 0;
  if (upperHead[i] !== 'CREATE' && upperHead[i] !== 'ALTER') return false;
  i += 1;
  if (upperHead[i] === 'OR') {
    i += 1;
    if (upperHead[i] !== 'ALTER') return false;
    i += 1;
  }
  return (
    upperHead[i] === 'PROC' ||
    upperHead[i] === 'PROCEDURE' ||
    upperHead[i] === 'FUNCTION'
  );
}

/**
 * True when the cursor follows a `CREATE [OR ALTER] PROC[EDURE]` parenless
 * parameter header (T-SQL stored procedures allow declaring parameters
 * without surrounding parens, e.g. `CREATE PROCEDURE foo @p1 INT, @p2 INT
 * AS ...`). The cursor's `@`-local must appear after the procedure name
 * and before the `AS` keyword that opens the body.
 */
function followsCreateProcedureHeader(tokens: readonly Token[]): boolean {
  // Must NOT be inside a paren — that case is covered by
  // `isInsideCreateRoutineParamList`.
  if (findEnclosingLparenIndex(tokens) !== -1) return false;
  // Walk back from the cursor; we must hit CREATE/ALTER + (OR ALTER)? +
  // PROC|PROCEDURE in that order before any statement terminator or `AS`.
  let sawProc = false;
  let sawCreateOrAlter = false;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    const u = identUpper(t);
    if (u === 'AS') return false; // Inside body, not header.
    if (t.type === 'semicolon') return false;
    if (u === 'PROC' || u === 'PROCEDURE') {
      sawProc = true;
      continue;
    }
    if ((u === 'CREATE' || u === 'ALTER') && sawProc) {
      sawCreateOrAlter = true;
      break;
    }
  }
  return sawCreateOrAlter;
}

/**
 * True when the cursor's most recent DECLARE keyword precedes the `@`-local
 * AND there is no enclosing paren between them (the scalar-local form, not
 * the `DECLARE @t TABLE(...)` form which is handled by
 * `isInsideCreateTableColumnList`).
 */
function followsDeclare(tokens: readonly Token[]): boolean {
  // Walk back from the cursor; if we hit a DECLARE before any `(` /
  // statement terminator, this is a scalar DECLARE.
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    const t = tokens[i]!;
    const u = identUpper(t);
    if (u === 'DECLARE') return true;
    if (
      t.type === 'lparen' ||
      t.type === 'semicolon' ||
      u === 'SELECT' ||
      u === 'INSERT' ||
      u === 'UPDATE' ||
      u === 'DELETE' ||
      u === 'BEGIN' ||
      u === 'END'
    ) {
      return false;
    }
  }
  return false;
}
